import { parseEdgeChain } from './flowchart-parser-edge-chain';
import { FlowchartLabelOrigin } from './flowchart-parser-model';
import { parseNodeExpression } from './flowchart-parser-node-expression';
import {
  currentParent,
  commitParsedEdge,
  upsertNode,
  type FlowchartParserState,
} from './flowchart-parser-state';
import {
  handleClassDefLine,
  handleClassStatementLine,
  handleStyleStatementLine,
} from './flowchart-parser-style-statements';
import { isSubgraphEndLine, isSubgraphHeaderLine, pushSubgraph } from './flowchart-parser-subgraphs';

interface FlowchartLineHandlerInput {
  line: string;
  state: FlowchartParserState;
}

type FlowchartLineHandler = (input: FlowchartLineHandlerInput) => boolean;

const DIRECTION_REGEX = /^direction\s+(TD|TB|LR|RL|BT)\b/i;
const LINKSTYLE_REGEX = /^linkStyle\b/i;
const CLICK_REGEX = /^click\b/i;

function handleSubgraphHeader(input: FlowchartLineHandlerInput): boolean {
  if (!isSubgraphHeaderLine(input.line)) {
    return false;
  }
  pushSubgraph({ line: input.line, state: input.state });
  return true;
}

function handleSubgraphEnd(input: FlowchartLineHandlerInput): boolean {
  if (!isSubgraphEndLine(input.line)) {
    return false;
  }
  input.state.stack.pop();
  return true;
}

function handleDirectionDirective(input: FlowchartLineHandlerInput): boolean {
  if (!DIRECTION_REGEX.test(input.line)) {
    return false;
  }
  if (input.state.stack.length > 0) {
    input.state.unsupportedDirectives.push(input.line);
  }
  return true;
}

function handleLinkStyleDirective(input: FlowchartLineHandlerInput): boolean {
  if (!LINKSTYLE_REGEX.test(input.line)) {
    return false;
  }
  input.state.unsupportedDirectives.push(input.line);
  return true;
}

function handleClickDirective(input: FlowchartLineHandlerInput): boolean {
  return CLICK_REGEX.test(input.line);
}

function handleEdgeChain(input: FlowchartLineHandlerInput): boolean {
  const parsedChain = parseEdgeChain(input.line);
  if (!parsedChain) {
    return false;
  }
  parsedChain.refs.forEach((ref) =>
    upsertNode({
      nodesById: input.state.nodesById,
      ref,
      parent: currentParent(input.state.stack),
      pendingClassNamesById: input.state.pendingClassNamesById,
      pendingStylesById: input.state.pendingStylesById,
    }),
  );
  parsedChain.edges.forEach((parsedEdge) =>
    commitParsedEdge({
      parsedEdge,
      edges: input.state.edges,
      pendingClassNamesById: input.state.pendingClassNamesById,
    }),
  );
  return true;
}

function handleNodeDeclaration(input: FlowchartLineHandlerInput): boolean {
  const parsedNode = parseNodeExpression({
    expression: input.line,
    origin: FlowchartLabelOrigin.BareDeclaration,
  });
  if (parsedNode) {
    upsertNode({
      nodesById: input.state.nodesById,
      ref: parsedNode,
      parent: currentParent(input.state.stack),
      pendingClassNamesById: input.state.pendingClassNamesById,
      pendingStylesById: input.state.pendingStylesById,
    });
  }
  return true;
}

const FLOWCHART_LINE_HANDLERS: ReadonlyArray<FlowchartLineHandler> = [
  handleSubgraphHeader,
  handleSubgraphEnd,
  handleDirectionDirective,
  handleClassDefLine,
  handleClassStatementLine,
  handleStyleStatementLine,
  handleLinkStyleDirective,
  handleClickDirective,
  handleEdgeChain,
  handleNodeDeclaration,
];

export function commitFlowchartLine(input: FlowchartLineHandlerInput): void {
  FLOWCHART_LINE_HANDLERS.some((handler) => handler(input));
}
